import type { AssetSheetColumnMapEntryDTO, AssetSheetColumnMapTabDTO, AssetSheetRowReportDTO } from '@atlas/types'
import { AssetSheetDerivedRowStatus, AssetSheetLevel, AxisCode } from '@atlas/types'
import {
	EmptyState,
	ErrorState,
	FieldShell,
	ListSkeleton,
	PaginationFooter,
	SelectField,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@atlas/ui-components'
import { Button, cn, IconButton, Input } from '@atlas/ui-primitives'
import { ChevronDown } from 'lucide-react'
import { Fragment, useState } from 'react'
import { Navigate, useSearchParams } from 'react-router-dom'
import { useAssetSheetRows, useRetryAssetSheetAsset } from '@/hooks/useAssetSheet'
import { useAssetSheetColumnMap } from '@/hooks/useAssetSheetColumnMap'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { apiErrorMessage } from '@/lib/api-error'
import { notification } from '@/notifications/notification-store'
import { AssetSheetParam, statusOf } from './asset-sheet-view'
import { RowDetail } from './RowDetail'
import { RowStatusBadge, rowStatusLabel } from './RowStatusBadge'

const SEARCH_DEBOUNCE_MS = 300
const COLUMNS = 7

const STATUS_OPTIONS = Object.values(AssetSheetDerivedRowStatus).map((s) => ({ value: s, label: rowStatusLabel(s) }))

/** Every tracked row, filtered by tab, status and asset code in the URL, each expandable to its detail inline. */
export function RowsTab() {
	const filters = useRowFilters()
	const assetCode = useDebouncedValue(filters.query.trim(), SEARCH_DEBOUNCE_MS)
	const rows = useAssetSheetRows({
		tab: filters.sheetTab || undefined,
		status: filters.status,
		assetCode: assetCode || undefined,
		page: filters.page,
	})

	return (
		<div className="flex flex-col gap-ds-4">
			<RowFilterBar filters={filters} />
			{rows.isPending ? (
				<ListSkeleton label="Loading rows" />
			) : rows.isError ? (
				<ErrorState
					title="Could not load the rows"
					description={apiErrorMessage(rows.error, 'Try again.')}
					action={
						<Button variant="secondary" size="sm" onPress={() => void rows.refetch()}>
							Try again
						</Button>
					}
				/>
			) : rows.data.data.length === 0 && rows.data.pagination.total > 0 ? (
				<Navigate replace to={{ search: filters.searchAt(rows.data.pagination.totalPages) }} />
			) : rows.data.data.length === 0 ? (
				<EmptyState
					variant={filters.isFiltered ? 'filtered' : 'first-run'}
					title={filters.isFiltered ? 'No rows match these filters' : 'No rows yet'}
					description={filters.isFiltered ? undefined : 'Rows appear here after the first sync.'}
					action={
						filters.isFiltered && (
							<Button variant="secondary" size="sm" onPress={filters.clear}>
								Clear filters
							</Button>
						)
					}
				/>
			) : (
				<div>
					<RowsTable rows={rows.data.data} isStale={rows.isPlaceholderData} />
					<PaginationFooter
						page={filters.page}
						totalPages={rows.data.pagination.totalPages}
						total={rows.data.pagination.total}
						noun="row"
						nounPlural="rows"
						isFetching={rows.isPlaceholderData}
						onPage={(next) => filters.set(AssetSheetParam.PAGE, String(next))}
					/>
				</div>
			)}
		</div>
	)
}

type RowFilters = ReturnType<typeof useRowFilters>

/** The Rows tab's filters as the URL holds them, and setters that write them back. */
function useRowFilters() {
	const [params, setParams] = useSearchParams()
	const sheetTab = params.get(AssetSheetParam.SHEET_TAB) ?? ''
	const status = statusOf(params.get(AssetSheetParam.STATUS))
	const query = params.get(AssetSheetParam.QUERY) ?? ''

	// A filter change starts again at page 1; typing replaces the history entry rather than adding one per key.
	const set = (key: AssetSheetParam, value: string, replace = false) =>
		setParams(
			(prev) => {
				const next = new URLSearchParams(prev)
				if (value) next.set(key, value)
				else next.delete(key)
				if (key !== AssetSheetParam.PAGE) next.delete(AssetSheetParam.PAGE)
				return next
			},
			{ replace },
		)
	/** The URL's query string with the page at `page`, to land a page past the last one on the last. */
	const searchAt = (page: number) => {
		const next = new URLSearchParams(params)
		next.set(AssetSheetParam.PAGE, String(page))
		return `?${next}`
	}
	const clear = () =>
		setParams((prev) => new URLSearchParams({ [AssetSheetParam.VIEW]: prev.get(AssetSheetParam.VIEW) ?? '' }))

	return {
		sheetTab,
		status,
		query,
		page: Math.trunc(Number(params.get(AssetSheetParam.PAGE))) || 1,
		isFiltered: Boolean(sheetTab || status || query),
		set,
		searchAt,
		clear,
	}
}

function RowFilterBar({ filters }: { filters: RowFilters }) {
	const tabs = useAssetSheetColumnMap().data?.map((t) => ({ value: t.tab, label: t.tab })) ?? []
	return (
		<div className="grid gap-ds-3 sm:grid-cols-[10rem_12rem_minmax(0,1fr)] sm:items-end">
			<SelectField
				label="Tab"
				value={filters.sheetTab}
				options={tabs}
				placeholder="All tabs"
				onChange={(v) => filters.set(AssetSheetParam.SHEET_TAB, v)}
			/>
			<SelectField
				label="Status"
				value={filters.status ?? ''}
				options={STATUS_OPTIONS}
				placeholder="All statuses"
				onChange={(v) => filters.set(AssetSheetParam.STATUS, v)}
			/>
			<FieldShell label="Asset code" htmlFor="rows-asset-code" className="text-ds-sm">
				<Input
					id="rows-asset-code"
					type="search"
					size="sm"
					value={filters.query}
					placeholder="Part of an asset code, e.g. ASSET-12"
					onChange={(e) => filters.set(AssetSheetParam.QUERY, e.target.value, true)}
				/>
			</FieldShell>
		</div>
	)
}

/** The rows of one page, each opening to its detail beneath it. */
function RowsTable({ rows, isStale }: { rows: AssetSheetRowReportDTO[]; isStale: boolean }) {
	const [open, setOpen] = useState<ReadonlySet<string>>(new Set())
	const retry = useRetryAssetSheetAsset()

	const toggle = (id: string) =>
		setOpen((prev) => {
			const next = new Set(prev)
			if (!next.delete(id)) next.add(id)
			return next
		})

	const onRetry = (code: string) =>
		retry.mutate(code, {
			onSuccess: () => notification.success(`${code} is downloading again.`),
			onError: (error) => notification.error(apiErrorMessage(error, `${code} could not be retried.`)),
		})

	return (
		<div className="overflow-x-auto rounded-ds-card border border-ds-border bg-ds-surface-elevated">
			<Table className={cn('min-w-2xl', isStale && 'opacity-60')}>
				<TableHeader>
					<TableRow>
						<TableHead>Row</TableHead>
						<TableHead>Excel line</TableHead>
						<TableHead>Brand</TableHead>
						<TableHead>Product</TableHead>
						<TableHead>Assets</TableHead>
						<TableHead>Status</TableHead>
						<TableHead>
							<span className="sr-only">Details</span>
						</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{rows.map((row) => (
						<Fragment key={row.id}>
							<RowLine row={row} isOpen={open.has(row.id)} onToggle={() => toggle(row.id)} />
							{open.has(row.id) && (
								<TableRow className="bg-ds-surface hover:bg-ds-surface">
									<TableCell colSpan={COLUMNS} className="p-ds-4">
										<RowDetail row={row} onRetry={onRetry} />
									</TableCell>
								</TableRow>
							)}
						</Fragment>
					))}
				</TableBody>
			</Table>
		</div>
	)
}

/**
 * The name of the tab's column for a level of the brand axis: the root column names the brand, a
 * column read under another names the product, as the sync reads them.
 */
function brandColumnOf(tabs: AssetSheetColumnMapTabDTO[], tab: string, level: AssetSheetLevel): string | undefined {
	const columns = tabs.find((t) => t.tab === tab)?.columns ?? []
	const isLevel = (c: AssetSheetColumnMapEntryDTO) =>
		level === AssetSheetLevel.PRODUCT ? Boolean(c.cellFormat.parentColumn) : Boolean(c.cellFormat.rootsOnly)
	return columns.find((c) => c.axis === AxisCode.BRAND && !c.objectType && isLevel(c))?.columnName
}

/** What the row's Excel cell says at a level of the brand axis, matched or not; the detail says what it matched. */
function BrandCell({ row, level }: { row: AssetSheetRowReportDTO; level: AssetSheetLevel }) {
	const tabs = useAssetSheetColumnMap().data ?? []
	const column = brandColumnOf(tabs, row.tab, level)
	const text = column ? row.cells[column]?.trim() : undefined
	return text ? <span className="whitespace-pre-line">{text}</span> : <span className="text-ds-text-muted">–</span>
}

function RowLine({ row, isOpen, onToggle }: { row: AssetSheetRowReportDTO; isOpen: boolean; onToggle: () => void }) {
	const assets = row.objects.filter((o) => !o.removed).length
	return (
		<TableRow className="cursor-pointer" onClick={onToggle}>
			<TableCell className="font-ds-mono text-ds-xs text-ds-text">{row.rowKey}</TableCell>
			<TableCell className="tabular-nums text-ds-text-muted">{row.rowNumber}</TableCell>
			<TableCell>
				<BrandCell row={row} level={AssetSheetLevel.BRAND} />
			</TableCell>
			<TableCell>
				<BrandCell row={row} level={AssetSheetLevel.PRODUCT} />
			</TableCell>
			<TableCell className="tabular-nums">{assets}</TableCell>
			<TableCell>
				<RowStatusBadge status={row.derivedStatus} />
			</TableCell>
			<TableCell className="w-12 text-right">
				<IconButton
					variant="muted"
					size="sm"
					aria-expanded={isOpen}
					aria-label={`${isOpen ? 'Hide' : 'Show'} the details of ${row.rowKey}`}
					onPress={onToggle}
				>
					<ChevronDown aria-hidden className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-180')} />
				</IconButton>
			</TableCell>
		</TableRow>
	)
}
